import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, apiGet } from "../api";
import { toast } from "sonner";
import { PriceFileUploadDialog } from "../components/PriceFileUploadDialog";
import usePageTitle from "../hooks/usePageTitle";
import { DataTable } from "../components/dc";
import { formatNumber } from "../lib/format";

// Price Files view (Data hub sub-tab). Missing-files banner + upload dialog +
// searchable/paginated files table with delete, matching the reference design.
// Endpoints preserved: /price-files/summary, /missing-price-files, /suppliers,
// DELETE /price-files/{id}.
export default function PriceFiles() {
  usePageTitle("Price Files");
  const [priceFiles, setPriceFiles] = useState([]);
  const [missingFiles, setMissingFiles] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const loadPriceFiles = useCallback(async () => {
    const data = await apiGet("/price-files/summary");
    setPriceFiles(Array.isArray(data) ? data : []);
  }, []);
  const loadMissingFiles = useCallback(async () => {
    const data = await apiGet("/missing-price-files");
    setMissingFiles(Array.isArray(data) ? data : []);
  }, []);
  const loadSuppliers = useCallback(async () => {
    const data = await apiGet("/suppliers");
    setSuppliers(Array.isArray(data?.suppliers) ? data.suppliers : []);
  }, []);
  useEffect(() => {
    loadPriceFiles();
    loadMissingFiles();
    loadSuppliers();
  }, [loadPriceFiles, loadMissingFiles, loadSuppliers]);

  const handleDelete = async (fileId) => {
    if (!window.confirm("Delete this price file?")) return;
    try {
      setDeletingId(fileId);
      await axios.delete(`${API}/price-files/${fileId}`);
      toast.success("Price file deleted");
      loadPriceFiles();
      loadMissingFiles();
    } catch {
      toast.error("Failed to delete price file");
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    { key: "supplier", label: "Supplier", width: "1fr", render: (f) => <span style={{ fontWeight: 600 }}>{f.supplier}</span> },
    { key: "month_year", label: "Month", width: "0.7fr", numeric: true, color: "var(--color-text-3)" },
    { key: "item_count", label: "Products", width: "0.7fr", numeric: true, color: "var(--color-text-3)", render: (f) => formatNumber(f.item_count || 0) },
    { key: "original_filename", label: "File", width: "1.6fr", truncate: true, color: "#67e0f0", render: (f) => f.original_filename || "—" },
    { key: "created_at", label: "Uploaded", width: "0.8fr", color: "#b6a8d0", render: (f) => (f.created_at ? new Date(f.created_at).toLocaleDateString("en-GB") : "—") },
    { key: "_del", label: "", width: "0.6fr", align: "right", render: (f) => (
      <span
        data-testid={`delete-price-file-${f.id}`}
        onClick={() => handleDelete(f.id)}
        style={{ color: "#fb7185", fontWeight: 600, cursor: deletingId === f.id ? "wait" : "pointer", fontSize: 13, opacity: deletingId === f.id ? 0.5 : 1 }}
      >
        {deletingId === f.id ? "Deleting…" : "Delete"}
      </span>
    ) },
  ];

  return (
    <div data-testid="price-files-page">
      {missingFiles.length > 0 && (
        <div style={{ background: "var(--color-bg-hemocyanin)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: 14, padding: "14px 18px", marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#fbbf24", marginBottom: 8 }}>⚠ Missing price files</div>
          <div style={{ display: "grid", gap: 6 }}>
            {missingFiles.slice(0, 8).map((m) => (
              <div key={`${m.supplier}-${m.start_month}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--color-text-3)" }}>
                <span style={{ color: "#fbbf24" }}>{m.supplier} — {m.start_month === m.end_month ? m.start_month : `${m.start_month} to ${m.end_month}`}</span>
                <span data-testid={`missing-file-${m.supplier}`}>{formatNumber(m.total_invoice_count)} invoices</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <PriceFileUploadDialog
          suppliers={suppliers}
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
          onSuccess={() => { loadPriceFiles(); loadMissingFiles(); }}
        />
      </div>

      <DataTable
        title="Price Files"
        columns={columns}
        rows={priceFiles}
        perPage={8}
        searchKeys={["supplier", "original_filename", "month_year"]}
        searchPlaceholder="Search supplier or file name..."
        emptyText="No price files yet. Add one above."
      />
    </div>
  );
}
